import {
  AttachmentUploadType,
  ReceiptAttachmentModel,
} from 'src/models/receiptAttachment.models'
import {
  AttachmentVerification,
  ReceiptVerificationStatus,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'

export type RowBadge =
  | 'verified'
  | 'not-verified'
  | 'erp-success'
  | 'erp-failed'
  | 'auto-matched'
  | null

export type RowTone = 'default' | 'error'

export interface RowStatus {
  badge: RowBadge
  secondaryBadge?: RowBadge
  statusLabel: string
  // When present, the row renders these reason strings joined with ", " in
  // place of statusLabel. Each reason is a standalone translation key (e.g.
  // "Amount cannot be verified"); they must be translated individually rather
  // than as a joined string (which wouldn't match any locale entry).
  statusReasons?: string[]
  statusTone: RowTone
  textTone: RowTone
  showProgress: boolean
  progressTone: RowTone
}

export interface StatusForRowInput {
  uploadState?: 'uploading' | 'verifying'
  verification?: AttachmentVerification
  erpStatus?: ConnectedPlatformStatus
  erpIntegrationName?: string
  uploadType?: AttachmentUploadType
}

function negativeReasons(verification?: AttachmentVerification): string[] {
  return verification?.reasons?.filter((r) => r && r.length > 0) ?? []
}

function isVerifiedStatus(status?: ReceiptVerificationStatus): boolean {
  return (
    status === ReceiptVerificationStatus.Verified ||
    status === ReceiptVerificationStatus.AutoMatched
  )
}

function isNegativeStatus(status?: ReceiptVerificationStatus): boolean {
  return (
    status === ReceiptVerificationStatus.NotVerified ||
    status === ReceiptVerificationStatus.Mismatched ||
    status === ReceiptVerificationStatus.Unreadable
  )
}

function isAutoMatchUploadType(type?: AttachmentUploadType): boolean {
  return (
    type === AttachmentUploadType.APP ||
    type === AttachmentUploadType.EMAIL ||
    type === AttachmentUploadType.PHONE_MESSAGE
  )
}

export function statusForRow(input: StatusForRowInput): RowStatus {
  const {
    uploadState,
    verification,
    erpStatus,
    erpIntegrationName,
    uploadType,
  } = input

  if (uploadState === 'uploading') {
    return {
      badge: null,
      statusLabel: 'Loading...',
      statusTone: 'default',
      textTone: 'default',
      showProgress: true,
      progressTone: 'default',
    }
  }

  if (uploadState === 'verifying') {
    return {
      badge: null,
      statusLabel: 'Verifying...',
      statusTone: 'default',
      textTone: 'default',
      showProgress: true,
      progressTone: 'default',
    }
  }

  if (verification?.status === ReceiptVerificationStatus.Verifying) {
    return {
      badge: null,
      statusLabel: 'Verifying...',
      statusTone: 'default',
      textTone: 'default',
      showProgress: true,
      progressTone: 'default',
    }
  }

  const hasErpSuccess = erpStatus === ConnectedPlatformStatus.Success
  const hasErpFailed = erpStatus === ConnectedPlatformStatus.Failed
  const hasErp = hasErpSuccess || hasErpFailed
  const erpBadge: RowBadge = hasErpSuccess
    ? 'erp-success'
    : hasErpFailed
      ? 'erp-failed'
      : null

  if (hasErp && isNegativeStatus(verification?.status)) {
    const reasons = negativeReasons(verification)
    return {
      badge: 'not-verified',
      secondaryBadge: erpBadge,
      statusLabel:
        reasons.length > 0 ? reasons.join(', ') : 'Receipt not verified',
      statusReasons: reasons.length > 0 ? reasons : undefined,
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (hasErp && isVerifiedStatus(verification?.status)) {
    return {
      badge:
        verification?.status === ReceiptVerificationStatus.AutoMatched
          ? 'auto-matched'
          : 'verified',
      secondaryBadge: erpBadge,
      statusLabel:
        verification?.status === ReceiptVerificationStatus.AutoMatched
          ? 'Auto-matched'
          : 'Receipt verified',
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (hasErpFailed) {
    return {
      badge: 'erp-failed',
      statusLabel: 'Failed to sync, please try again',
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (hasErpSuccess) {
    return {
      badge: 'erp-success',
      statusLabel: erpIntegrationName ? 'Synced to ${integration}' : 'Synced',
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (isVerifiedStatus(verification?.status)) {
    if (verification?.status === ReceiptVerificationStatus.AutoMatched) {
      return {
        badge: 'auto-matched',
        statusLabel: 'Auto-matched',
        statusTone: 'default',
        textTone: 'default',
        showProgress: false,
        progressTone: 'default',
      }
    }
    return {
      badge: 'verified',
      statusLabel: 'Receipt verified',
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (isNegativeStatus(verification?.status)) {
    const reasons = negativeReasons(verification)
    return {
      badge: 'not-verified',
      statusLabel:
        reasons.length > 0 ? reasons.join(', ') : 'Receipt not verified',
      statusReasons: reasons.length > 0 ? reasons : undefined,
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  if (isAutoMatchUploadType(uploadType)) {
    return {
      badge: 'auto-matched',
      statusLabel: 'Auto-matched',
      statusTone: 'default',
      textTone: 'default',
      showProgress: false,
      progressTone: 'default',
    }
  }

  return {
    badge: null,
    statusLabel: 'Complete',
    statusTone: 'default',
    textTone: 'default',
    showProgress: false,
    progressTone: 'default',
  }
}

// Convenience for a persisted attachment. Derives verification from the
// backend's `matchingStatuses` booleans when the `verification` field isn't
// directly populated.
export function statusForAttachment(
  attachment: ReceiptAttachmentModel,
  opts: {
    erpStatus?: ConnectedPlatformStatus
    erpIntegrationName?: string
  } = {},
): RowStatus {
  return statusForRow({
    verification: resolveAttachmentVerification(attachment),
    erpStatus: opts.erpStatus,
    erpIntegrationName: opts.erpIntegrationName,
    uploadType: attachment.uploadType,
  })
}
